/**
 * @file catalog-registry-query.ts
 * @description Состояние реестра товаров в адресе и отбор списка
 * (phase19.31, зона «Товары», макет (а)).
 *
 * Фильтры, вид («Таблица / Плитки») и страница живут в адресе: ссылкой на
 * отобранный список можно поделиться, «назад» из карточки товара возвращает
 * туда же. Сервер отдаёт товары магазина целиком (`withStock=1`), поэтому
 * отбор и счётчики пилюль считаются здесь — одним проходом, без запроса на
 * каждое переключение.
 *
 * Пилюли (SC45) — один ряд без дублей «Все»: наличие, «Нигде не опубликован»,
 * «Без фото». Выпадающие списки — категория (с потомками), публикация, вид.
 */

import { isProductLowStock, isProductOutOfStock, type CatalogCategory, type CatalogProduct } from "./api-catalog";
import { categoryWithDescendants } from "./catalog-category-tree";
import { productSiteState, type ProductSiteState } from "./catalog-status";
import { positiveInt } from "./list-query";

export const PRODUCTS_PAGE_SIZES = [25, 50, 100] as const;
export const PRODUCTS_DEFAULT_PAGE_SIZE = 50;

/** Пилюли отбора. `all` — без отбора. */
export type ProductsChip = "all" | "out" | "low" | "nowhere" | "nophoto";
const CHIPS: readonly ProductsChip[] = ["all", "out", "low", "nowhere", "nophoto"];

export type ProductsView = "table" | "tiles";

const SITE_STATES: readonly ProductSiteState[] = ["published", "hidden", "draft", "archived"];

export interface ProductsRegistryQuery {
  search: string;
  /** Категория вместе с потомками; "" — все. */
  categoryId: string;
  /** Состояние на сайте; "" — любое. */
  site: ProductSiteState | "";
  /** Действующий вид товара (W22); "" — любой. */
  kindId: string;
  chip: ProductsChip;
  view: ProductsView;
  page: number;
  pageSize: number;
}

/** Имена параметров адреса — одним реестром. */
const KEY = {
  search: "q",
  categoryId: "category",
  site: "site",
  kindId: "kind",
  chip: "only",
  view: "view",
  page: "page",
  pageSize: "size",
} as const;

/** Адрес → состояние. Неизвестные значения тихо заменяются умолчаниями. */
export function parseProductsQuery(params: URLSearchParams): ProductsRegistryQuery {
  const site = params.get(KEY.site) ?? "";
  const chip = params.get(KEY.chip) ?? "all";
  const size = positiveInt(params.get(KEY.pageSize), PRODUCTS_DEFAULT_PAGE_SIZE);
  return {
    search: (params.get(KEY.search) ?? "").trim(),
    categoryId: params.get(KEY.categoryId) ?? "",
    site: (SITE_STATES as readonly string[]).includes(site) ? (site as ProductSiteState) : "",
    kindId: params.get(KEY.kindId) ?? "",
    chip: (CHIPS as readonly string[]).includes(chip) ? (chip as ProductsChip) : "all",
    view: params.get(KEY.view) === "tiles" ? "tiles" : "table",
    page: positiveInt(params.get(KEY.page), 1),
    pageSize: (PRODUCTS_PAGE_SIZES as readonly number[]).includes(size) ? size : PRODUCTS_DEFAULT_PAGE_SIZE,
  };
}

/** Состояние → адрес; значения по умолчанию не пишем. */
export function writeProductsQuery(query: ProductsRegistryQuery): URLSearchParams {
  const params = new URLSearchParams();
  if (query.search) params.set(KEY.search, query.search);
  if (query.categoryId) params.set(KEY.categoryId, query.categoryId);
  if (query.site) params.set(KEY.site, query.site);
  if (query.kindId) params.set(KEY.kindId, query.kindId);
  if (query.chip !== "all") params.set(KEY.chip, query.chip);
  if (query.view === "tiles") params.set(KEY.view, "tiles");
  if (query.page > 1) params.set(KEY.page, String(query.page));
  if (query.pageSize !== PRODUCTS_DEFAULT_PAGE_SIZE) params.set(KEY.pageSize, String(query.pageSize));
  return params;
}

/** Поменять часть состояния; смена отбора возвращает на первую страницу. */
export function patchProductsQuery(
  query: ProductsRegistryQuery,
  patch: Partial<ProductsRegistryQuery>,
): ProductsRegistryQuery {
  const keepPage = Object.keys(patch).every((key) => key === "page" || key === "view");
  return { ...query, ...patch, page: keepPage ? (patch.page ?? query.page) : 1 };
}

/** Сбросить отбор, сохранив вид и размер страницы. */
export function resetProductsFilters(query: ProductsRegistryQuery): ProductsRegistryQuery {
  return { ...query, search: "", categoryId: "", site: "", kindId: "", chip: "all", page: 1 };
}

/** Сколько выпадающих фильтров включено (число на «Фильтры · N» телефона). */
export function productsSheetFilterCount(query: ProductsRegistryQuery): number {
  return [query.categoryId, query.site, query.kindId].filter(Boolean).length;
}

/** Включён ли хоть какой-то отбор (для текста «пусто под фильтром»). */
export function productsFiltered(query: ProductsRegistryQuery): boolean {
  return query.search !== "" || query.chip !== "all" || productsSheetFilterCount(query) > 0;
}

/** Подходит ли товар под пилюлю. */
export function matchesChip(product: CatalogProduct, chip: ProductsChip): boolean {
  switch (chip) {
    case "out":
      return isProductOutOfStock(product);
    case "low":
      return isProductLowStock(product);
    case "nowhere":
      return product.publishedChannels.length === 0;
    case "nophoto":
      return product.primaryImageUrl === null;
    default:
      return true;
  }
}

/** Поиск: название, адрес страницы и артикулы фасовок. */
function matchesSearch(product: CatalogProduct, needle: string): boolean {
  if (!needle) return true;
  const hay = [product.displayName, product.slug, ...(product.skuCodes ?? [])];
  return hay.some((text) => text.toLocaleLowerCase("ru").includes(needle));
}

export interface ProductsSelection {
  /** Отобрано всеми фильтрами (до разбиения на страницы). */
  rows: CatalogProduct[];
  /** Счётчики пилюль — по списку без самой пилюли (иначе «Мало 5» = «5 из 5»). */
  chipCounts: Record<ProductsChip, number>;
}

/** Отбор списка и счётчики пилюль одним проходом. */
export function selectProducts(
  products: CatalogProduct[],
  categories: CatalogCategory[],
  query: ProductsRegistryQuery,
): ProductsSelection {
  const needle = query.search.toLocaleLowerCase("ru");
  const inCategory = query.categoryId ? categoryWithDescendants(categories, query.categoryId) : null;
  const base = products.filter(
    (p) =>
      matchesSearch(p, needle) &&
      (!inCategory || inCategory.has(p.categoryId)) &&
      (!query.site || productSiteState(p) === query.site) &&
      (!query.kindId || p.effectiveKind?.id === query.kindId),
  );
  const chipCounts = Object.fromEntries(
    CHIPS.map((chip) => [chip, base.filter((p) => matchesChip(p, chip)).length]),
  ) as Record<ProductsChip, number>;
  return { rows: base.filter((p) => matchesChip(p, query.chip)), chipCounts };
}

/** Страница списка + границы «1–50 из 312». */
export function productsPage<T>(rows: T[], page: number, pageSize: number) {
  const pages = Math.max(1, Math.ceil(rows.length / pageSize));
  const current = Math.min(page, pages);
  const start = (current - 1) * pageSize;
  return {
    items: rows.slice(start, start + pageSize),
    page: current,
    pages,
    from: rows.length === 0 ? 0 : start + 1,
    to: Math.min(rows.length, start + pageSize),
  };
}
